/**
 * Every word of the help centre, in one place.
 *
 * Kept as data rather than spread across components so the whole body of
 * product claims can be read - and tested - as one document. `truthfulness`
 * and `helpContent.test` both walk this array; a claim added in a component
 * somewhere would escape both.
 *
 * The rules this content is written under, all of them earned:
 *
 * **No number that was not calibrated.** Two reconstruction datasets have been
 * measured on this system. A head-turn video where the camera barely moved and
 * the subject rotated registered every frame and reconstructed the *room*. A
 * twenty-four photograph orbit where the camera moved but stopped at eight
 * positions came apart into eight disconnected pieces. Together those support
 * "hold the subject still, move the camera, keep going" and support nothing
 * about degrees. "Her 10 derecede bir fotoğraf çekin" would be a fabricated
 * threshold, and it is the kind of sentence a help page invents most easily.
 *
 * **No capability that does not exist.** Dense stereo, mesh and texture are
 * CUDA-only and no available build has them, so they are described as stages
 * that cannot run here rather than as stages that are waiting. HEIC cannot be
 * decoded by this stack. There is no e-mail, no updater, no export.
 *
 * **Absence is not failure.** "Bilinmiyor" means nothing could be measured;
 * out-of-scope regions mean the model does not assess them at all. Collapsing
 * either into "yetersiz" would send someone to reshoot something that was
 * never being judged.
 *
 * No `dangerouslySetInnerHTML` anywhere: every topic body is JSX, so nothing
 * here can inject markup even if a future author pastes something from a
 * document.
 */

import type { ComponentType, ReactNode } from "react";
import {
  IconAlert,
  IconCamera,
  IconCheck,
  IconCube,
  IconEye,
  IconFiles,
  IconFolder,
  IconHelp,
  IconHome,
  IconImage,
  IconLock,
  IconPerson,
  IconPhone,
  IconTarget,
  IconVideo,
} from "@/components/icons";
import { Callout, Loading } from "@/components/ui";
import { useMediaCapabilities } from "@/lib/useMediaCapabilities";

export interface HelpTopic {
  /** Stable slug. Used in `/yardim?konu=…`, so renaming one breaks a link. */
  id: string;
  title: string;
  icon: ComponentType<{ size?: number }>;
  /** Extra words the topic filter should match, beyond the title. */
  keywords: string[];
  body: () => ReactNode;
}

/** The WRONG/RECOMMENDED pair the capture guidance is built around. */
function Compare({
  wrong,
  wrongWhy,
  right,
  rightWhy,
}: {
  wrong: string;
  wrongWhy: string;
  right: string;
  rightWhy: string;
}) {
  return (
    <div className="grid-2 help__compare">
      <div className="help__case help__case--wrong">
        <p className="help__case-title">
          <IconAlert size={18} /> Yanlış
        </p>
        <p className="help__case-lead">{wrong}</p>
        <p className="muted small">{wrongWhy}</p>
      </div>
      <div className="help__case help__case--right">
        <p className="help__case-title">
          <IconCheck size={18} /> Önerilen
        </p>
        <p className="help__case-lead">{right}</p>
        <p className="muted small">{rightWhy}</p>
      </div>
    </div>
  );
}

/** One troubleshooting entry: the symptom, then what to actually do. */
function Problem({ symptom, children }: { symptom: string; children: ReactNode }) {
  return (
    <div className="help__problem">
      <h3 className="help__problem-title">{symptom}</h3>
      <div className="muted">{children}</div>
    </div>
  );
}

/**
 * The accepted formats, read from the server rather than written here.
 *
 * The help centre is the third place that used to keep its own copy of the
 * format list, after the uploader and the backend validator. Three lists is
 * three chances to tell a user their file works when it does not - so this one
 * asks `/media/capabilities`, which is built from the same table the upload
 * endpoint validates against.
 */
function LiveFormats({ mediaType }: { mediaType: "photo" | "video" }) {
  const capabilities = useMediaCapabilities();
  if (!capabilities.loaded) return <Loading label="Biçimler okunuyor" />;

  const formats = mediaType === "photo" ? capabilities.photos : capabilities.videos;
  return (
    <ul className="help__list">
      {formats.map((format) => (
        <li key={format.label}>
          <strong>{format.label}</strong> —{" "}
          <code>{format.extensions.map((e) => `.${e}`).join(" ")}</code>
          {format.support !== "reconstruct" && (
            <>
              {" "}
              <em>(rekonstrüksiyonda kullanılmaz)</em>
            </>
          )}
          {format.note && <div className="muted small">{format.note}</div>}
        </li>
      ))}
    </ul>
  );
}

export const HELP_TOPICS: HelpTopic[] = [
  {
    id: "baslangic",
    title: "Başlangıç",
    icon: IconHome,
    keywords: ["genel bakış", "nasıl çalışır", "akış", "adımlar"],
    body: () => (
      <>
        <p className="muted">
          SentyCap, bir konuyu farklı açılardan çekilmiş görüntülerden 3B olarak
          yeniden kurmaya çalışan bir çekim ve analiz aracıdır. Sihirli bir kutu
          değildir: ürettiği her sonuç, verdiğiniz görüntülerde gerçekten bulunan
          bilgiyle sınırlıdır.
        </p>
        <p className="muted">Çalışma sırası her projede aynıdır:</p>
        <ol className="help__steps">
          <li>
            <strong>Proje</strong> oluşturursunuz. Proje, tek bir konuyu temsil
            eder ve zamanla biriken bütün çekimleri bir arada tutar.
          </li>
          <li>
            <strong>Çekim</strong> eklersiniz: fotoğraf yükleyerek, video
            yükleyerek veya telefonunuzu bağlayıp doğrudan çekerek.
          </li>
          <li>
            <strong>Analiz</strong> çalıştırırsınız. Analiz, elinizdeki
            görüntülerin hangi bölgeler için yeterli kanıt taşıdığını ölçer.
          </li>
          <li>
            <strong>Kapsama</strong> sonucunu okur, eksik kalan bölgeler için
            düzeltme çekimi yaparsınız.
          </li>
          <li>
            <strong>Rekonstrüksiyon</strong> başlatırsınız. Sonuç, kamera
            konumları ve seyrek bir nokta bulutudur.
          </li>
        </ol>
        <Callout tone="neutral">
          Adımlar zorunlu bir kilit değildir: analiz çalıştırmadan da
          rekonstrüksiyon başlatabilirsiniz. Sıra bir öneridir, çünkü kapsaması
          zayıf bir çekimden iyi bir rekonstrüksiyon çıkmaz.
        </Callout>
      </>
    ),
  },
  {
    id: "proje-olusturma",
    title: "Proje oluşturma",
    icon: IconFolder,
    keywords: ["yeni proje", "tür", "insan", "nesne", "mekân", "sahne"],
    body: () => (
      <>
        <p className="muted">
          Yeni bir proje oluştururken bir ad ve bir konu türü seçersiniz. Tür üç
          üst başlıkta toplanır:
        </p>
        <ul className="help__list">
          <li>
            <strong>İnsan</strong> — Baş / Yüz, Tam Vücut. Kapsama analizi
            yalnızca bu grupta anlamlıdır; ölçüm modeli insan başı için
            tanımlanmıştır.
          </li>
          <li>
            <strong>Nesne</strong> — masaya sığan, çevresinde dolaşılabilen
            cisimler.
          </li>
          <li>
            <strong>Mekân / Sahne</strong> — oda, bina, açık alan.
          </li>
        </ul>
        <p className="muted">
          Tür, çekim ve rekonstrüksiyon davranışını değiştirmez; hangi analizlerin
          anlamlı olduğunu ve projelerinizin nasıl gruplandığını belirler. Bir
          projenin türü sonradan değiştirilemez.
        </p>
      </>
    ),
  },
  {
    id: "fotograf-cekimi",
    title: "Fotoğraf çekimi",
    icon: IconImage,
    keywords: ["fotoğraf", "yükleme", "kare", "jpeg"],
    body: () => (
      <>
        <p className="muted">
          Fotoğraf, rekonstrüksiyon için en doğrudan girdidir: her dosya bir
          görüştür ve hiçbir ara işlem gerektirmez. Bir çekim oturumu açar,
          dosyalarınızı yükler ve aynı projeye istediğiniz kadar oturum
          eklersiniz.
        </p>
        <Compare
          wrong="Aynı noktadan arka arkaya birçok fotoğraf çekmek."
          wrongWhy="Aynı yerden çekilen kareler yeni bilgi taşımaz. Ölçülen bir denemede sekiz noktada durup her noktada üçer fotoğraf çekmek, yirmi dört kare üretti ama eşleşme grafiği sekiz kopuk parçaya ayrıldı ve hiçbir şey kurulamadı."
          right="Durmadan ilerleyerek her karede biraz farklı bir yerden çekmek."
          rightWhy="Kamera gerçekten yer değiştirdiğinde ardışık kareler arasında hem örtüşme hem de derinlik bilgisi oluşur. Bu ikisi birlikte gerekir."
        />
        <p className="muted small">
          Kabul edilen fotoğraf biçimlerinin tam listesi{" "}
          <em>Desteklenen dosya türleri</em> başlığındadır.
        </p>
      </>
    ),
  },
  {
    id: "video-cekimi",
    title: "Video çekimi",
    icon: IconVideo,
    keywords: ["video", "kare seçimi", "hazırla", "mp4"],
    body: () => (
      <>
        <p className="muted">
          Video yükleyebilirsiniz, ancak rekonstrüksiyon videoyu doğrudan
          kullanmaz. Önce videodan kare seçilir: proje ekranındaki{" "}
          <strong>Videoları Hazırla</strong> adımı videoyu tarar ve
          birbirinden yeterince farklı, olabildiğince net kareleri ayırır.
          Hazırlanmamış bir video rekonstrüksiyona girmez.
        </p>
        <ul className="help__list">
          <li>Kareler zaman boyunca eşit aralıklarla seçilir, en baştan sırayla değil.</li>
          <li>Her aralıktan en net kare alınır; bulanık kareler elenir.</li>
          <li>Kamera hiç hareket etmediyse seçilecek farklı görüş de yoktur.</li>
        </ul>
        <Callout tone="caution">
          Yavaş ve düzgün ilerleyen bir çekim, hızlı çevrilen bir telefondan çok
          daha iyi sonuç verir. Hızlı hareket, tek tek karelerde hareket
          bulanıklığı üretir ve bulanık kare eşleştirilemez.
        </Callout>
        <p className="muted small">
          Kabul edilen video biçimlerinin tam listesi{" "}
          <em>Desteklenen dosya türleri</em> başlığındadır.
        </p>
      </>
    ),
  },
  {
    id: "telefon-ile-cekim",
    title: "Telefon ile çekim",
    icon: IconPhone,
    keywords: ["qr", "eşleştirme", "mobil", "kamera", "https"],
    body: () => (
      <>
        <p className="muted">
          Bir projenin <strong>Çekimler</strong> ekranından QR kod oluşturup
          telefonunuzla tarayarak telefonu SentyCap'e bağlayabilirsiniz.
          Telefona uygulama kurulmaz; açılan sayfa tarayıcıda çalışır.
        </p>
        <ul className="help__list">
          <li>Kodlar kısa süre geçerlidir ve yalnızca bir kez kullanılabilir.</li>
          <li>Bir bağlantı tek bir projeye yetki verir.</li>
          <li>
            Kalıcı olarak eşleştirilmiş cihaz saklanmaz; otomatik yeniden
            bağlanma yoktur. Bağlantı bittiğinde telefon hakkında hiçbir şey
            kalmaz.
          </li>
          <li>Bağlantıyı hem bilgisayardan hem telefondan sonlandırabilirsiniz.</li>
        </ul>
        <Callout tone="caution">
          Telefon kamerası yalnızca <strong>güvenli (HTTPS)</strong> bir adreste
          açılabilir. Tarayıcı kuralıdır ve aşılamaz. SentyCap'e HTTPS üzerinden
          bağlıysanız kamera çalışır; yerel ağ adresiyle (örneğin{" "}
          <code>http://192.168.…</code>) bağlandıysanız tarayıcı kamerayı
          vermez ve telefon sayfası bunu söyler. Bu durumda fotoğraflarınızı
          bilgisayardan yükleyebilirsiniz.
        </Callout>
      </>
    ),
  },
  {
    id: "canli-rehberlik",
    title: "Canlı Rehberlik",
    icon: IconTarget,
    keywords: ["rehberlik", "canlı", "yönlendirme", "telefon"],
    body: () => (
      <>
        <p className="muted">
          Telefon kamerasıyla çekim yaparken SentyCap, önizleme karelerini
          ölçüp tek bir yönlendirme cümlesi gösterir: yüz görünüyor mu, kadraj
          uygun mu, görüntü yeterince net ve aydınlık mı, bu açı daha önce
          alınmış mı.
        </p>
        <p className="muted">
          Rehberlik <strong>yardımcı bir araçtır, bir kapı değildir.</strong>{" "}
          Ne söylerse söylesin deklanşöre basabilirsiniz; hiçbir karenin
          çekilmesini engellemez. Rehberlik çalışmıyorsa çekim normal şekilde
          sürer.
        </p>
        <ul className="help__list">
          <li>
            Ölçülen şey geometridir: yüzün karede olup olmadığı ve kabaca hangi
            yöne baktığı. Kimlik, yaş, cinsiyet veya duygu çıkarımı yapılmaz.
          </li>
          <li>
            Açı, kalibre edilmiş bir derece değeri olarak değil, kaba yön
            kovaları olarak izlenir. Bu yüzden ekranda derece görmezsiniz.
          </li>
        </ul>
        <Callout tone="neutral">
          Rehberliğin "tamam" demesi, rekonstrüksiyonun başarılı olacağı anlamına
          gelmez. Tek tek karelerin uygunluğunu ölçer; sonucun tamamını değil.
        </Callout>
      </>
    ),
  },
  {
    id: "iyi-cekim",
    title: "İyi bir çekim nasıl yapılır?",
    icon: IconCamera,
    keywords: ["rehber", "açı", "örtüşme", "baseline", "ipucu"],
    body: () => (
      <>
        <p className="muted">
          Aşağıdaki ilkeler, bu sistemde ölçülen iki gerçek çekim denemesinden
          çıkarılmıştır. Kesin bir açı veya fotoğraf sayısı kuralı değildir.
        </p>

        <Compare
          wrong="Kamera sabit, kişi kafasını döndürüyor."
          wrongWhy="Kişi döndüğünde kafa, sahnenin geri kalanına göre hareket eden ayrı bir nesne olur. Çözüm sabit arka plana kilitlenir ve kafaya ait eşleşmeler elenir. Ölçülen denemede tüm kareler çözüldü; ortaya çıkan geometri odaydı, kafa değildi."
          right="Kişi sabit, kamera kişinin çevresinde hareket ediyor."
          rightWhy="Kamera gerçekten yer değiştirdiğinde kafa, farklı açılardan görülen sabit bir nesne olur. Ölçülen denemede bu kurulum, eşleşmelerin büyük çoğunluğunun kişi üzerinde olmasını sağladı."
        />

        <h3 className="help__subhead">Önerilen adımlar</h3>
        <ol className="help__steps">
          <li>Kişiyi nötr, rahat ve olabildiğince sabit bir pozisyonda durdurun.</li>
          <li>Kamera yüksekliğini göz hizasına yakın tutun.</li>
          <li>
            Kişinin çevresinde <strong>durmadan</strong> ilerleyerek çekim yapın.
            Birkaç noktada durup arka arkaya fotoğraf çekmek, aynı noktadan
            alınmış tekrar kareler üretir.
          </li>
          <li>
            Ardışık kareler arasında bol örtüşme bırakın: iki kare birbirine
            benzemeyecek kadar uzaksa eşleştirme kopar.
          </li>
          <li>Üst, alt ve eğik açılardan ek karelerle boşlukları tamamlayın.</li>
          <li>Işığı sabit tutun ve bulanık kareleri tekrar çekin.</li>
        </ol>

        <Callout tone="caution">
          Bu ilkeler yönlendiricidir. Belirli bir açı aralığı veya fotoğraf sayısı
          bu sistemde <strong>kalibre edilmemiştir</strong>, ve bu adımları
          izlemek başarılı bir rekonstrüksiyonu garanti etmez. İyi kapsama tek
          başına başarılı rekonstrüksiyon anlamına gelmez: kapsama neyin
          görüldüğünü ölçer, kareler arasındaki eşleşmenin kurulup kurulmadığını
          değil.
        </Callout>
      </>
    ),
  },
  {
    id: "analiz",
    title: "Analiz",
    icon: IconHelp,
    keywords: ["analiz", "ölçüm", "kalite", "yüz"],
    body: () => (
      <>
        <p className="muted">
          Analiz, yüklediğiniz görüntülerin geometrik yeterliliğini ölçer.
          Görüntü başına netlik ve pozlama; insan projelerinde ayrıca yüzün
          karede olup olmadığı ve kabaca hangi yöne baktığı ölçülür. Bu
          ölçümlerden proje düzeyinde bir kapsama sonucu üretilir.
        </p>
        <p className="muted">
          Analiz eş zamanlı çalışır: başlattığınızda sonucu aynı ekranda
          görürsünüz, arka planda kuyruğa girmez ve bu yüzden bildirim üretmez.
        </p>
        <Callout tone="neutral">
          Analiz bir 3B model üretmez ve rekonstrüksiyonun ön koşulu değildir.
          Ne yükleyebildiğinizi değil, yüklediğinizin ne kadarını
          ölçebildiğimizi anlatır.
        </Callout>
      </>
    ),
  },
  {
    id: "kapsama",
    title: "Kapsama",
    icon: IconTarget,
    keywords: ["kapsama", "bölge", "bilinmiyor", "yeterli", "eksik"],
    body: () => (
      <>
        <p className="muted">
          Kapsama, çekimlerinizin <strong>değerlendirilebilen</strong> bölgeleri
          ne kadar gördüğünü anlatır. Bir 3B model değildir ve rekonstrüksiyon
          kalitesini ölçmez; yalnızca elinizdeki görüntülerin hangi bölgeler için
          yeterli kanıt taşıdığını söyler.
        </p>
        <ul className="help__list">
          <li>
            <strong>Yeterli</strong> — bölge yeterli sayıda bağımsız görüşle
            değerlendirildi.
          </li>
          <li>
            <strong>Kısmi</strong> — bölge görüldü ancak kanıt sınırlı.
          </li>
          <li>
            <strong>Yetersiz</strong> — bölge değerlendirildi ve yeterli değil.
            Bu bir ölçüm sonucudur: ek çekim gerekir.
          </li>
          <li>
            <strong>Bilinmiyor</strong> — bölge hiç değerlendirilemedi. Bu bir
            başarısızlık <em>değildir</em>; ölçüm yapılamadığı anlamına gelir ve
            "yetersiz" ile aynı şey değildir.
          </li>
        </ul>
        <h3 className="help__subhead">Kapsam dışı alanlar</h3>
        <p className="muted">
          Bu sürümde başın üstü, başın arkası, ense ve çene altı{" "}
          <strong>hiç değerlendirilmez</strong>. Bunlar "bilinmiyor" da değildir:
          modelin kapsamı dışındadır ve ne kadar çekim yaparsanız yapın bir sonuç
          üretmezler. Saçla kapalı alanlar da ölçülemez.
        </p>
        <Callout tone="neutral">
          Beş bölgenin tamamı "yeterli" olsa bile bu, kafanın tamamının
          tarandığı anlamına gelmez — yalnızca değerlendirilen beş bölgenin
          yeterli olduğu anlamına gelir.
        </Callout>
        <h3 className="help__subhead">Kapsama, 3D model hazırlığı değildir</h3>
        <p className="muted">
          Bu ikisi farklı sorular sorar ve karıştırılmamalıdır.{" "}
          <strong>5 / 5 kapsama, 3D geometrinin tamamlandığı anlamına
          gelmez.</strong> Kapsama, beş yüz bölgesinin yeterli görülüp
          görülmediğini ölçer; bir 3D model ise başın <em>her yönünden</em>
          geometri ister — üstü, arkası, ensesi ve çene altı dahil. Bunlar
          kapsama modelinin hiç değerlendirmediği yerlerdir.
        </p>
        <p className="muted">
          Yani kapsaması "yeterli" çıkan bir çekimden eksik, delikli ya da
          yalnızca ön yüzü olan bir model çıkabilir. Kapsama iyi bir başlangıç
          göstergesidir, tamamlanmışlık garantisi değildir.
        </p>
      </>
    ),
  },
  {
    id: "rekonstruksiyon",
    title: "3D Rekonstrüksiyon",
    icon: IconCube,
    keywords: ["rekonstrüksiyon", "colmap", "nokta bulutu", "3b"],
    body: () => (
      <>
        <p className="muted">
          Rekonstrüksiyon, görüntülerinizdeki ortak noktaları eşleştirerek her
          karenin nereden çekildiğini çözer ve bu noktaların uzaydaki yerlerini
          hesaplar. Sonuç iki şeydir: kamera konumları ve seyrek bir nokta
          bulutu. Nokta bulutunu proje ekranındaki görüntüleyicide
          döndürebilir, yakınlaştırabilir ve modeli kaybederseniz
          <strong> Modele sığdır</strong> ile geri getirebilirsiniz.
        </p>
        <ul className="help__list">
          <li>
            Rekonstrüksiyon arka planda çalışır ve dakikalar sürebilir. Bittiğinde
            bildirim alırsınız.
          </li>
          <li>
            Girdi olarak fotoğraflarınız ve hazırlanmış videolardan seçilen
            kareler kullanılır. Çok fazla girdi olduğunda tüm diziye eşit
            aralıklarla yayılmış bir alt küme seçilir — ilk N kare değil.
          </li>
          <li>
            Hangi dosyaların kullanıldığı sonuca yazılır ve sonradan
            değiştirilemez.
          </li>
        </ul>
        <Callout tone="caution">
          Üretilen geometrinin ölçeği kalibre edilmemiştir. Milimetre veya
          santimetre cinsinden ölçüm yapılamaz.
        </Callout>
      </>
    ),
  },
  {
    id: "cikti-turleri",
    title: "Seyrek / Yoğun / Mesh / Doku",
    icon: IconCube,
    keywords: ["seyrek", "yoğun", "mesh", "doku", "sparse", "dense"],
    body: () => (
      <>
        <p className="muted">
          Bu dört kelime bir 3B üretim hattının dört farklı aşamasıdır ve sık
          karıştırılır:
        </p>
        <ul className="help__list">
          <li>
            <strong>Seyrek 3D</strong> — çözülmüş kamera konumları ve
            eşleştirilebilen noktaların bulutu. Yüzey yoktur; uzayda dağılmış
            noktalar vardır.
          </li>
          <li>
            <strong>Yoğun 3D</strong> — çok daha fazla nokta içeren bulut. Hâlâ
            yüzey değildir.
          </li>
          <li>
            <strong>Mesh</strong> — noktalardan çıkarılmış yüzey geometrisi.
            Üçgenlerden oluşan gerçek bir model burada başlar.
          </li>
          <li>
            <strong>Doku</strong> — bu yüzeyin üzerine fotoğraflardan çıkarılan
            görünüm.
          </li>
        </ul>
        <Callout tone="neutral">
          Bu sunucuda yalnızca <strong>Seyrek 3D</strong> üretilebilir. Yoğun 3D,
          mesh ve doku aşamaları farklı bir donanım gerektirir; kuyrukta
          beklemezler, hiç çalıştırılamazlar. Seyrek bir nokta bulutu bitmiş bir
          model değildir.
        </Callout>
      </>
    ),
  },
  {
    id: "rekonstruksiyon-hatalari",
    title: "Rekonstrüksiyon neden başarısız olur?",
    icon: IconAlert,
    keywords: ["başarısız", "hata", "eşleşme", "kopuk"],
    body: () => (
      <>
        <p className="muted">
          Rekonstrüksiyon, girdiler yeterli olmadığında başarısız olur ya da
          yanlış şeyi kurar. Bu sistemde ölçülen ve tekrar eden nedenler:
        </p>
        <ul className="help__list">
          <li>
            <strong>Kamera hareket etmedi.</strong> Konu döndü, kamera sabit
            kaldı. Bu durumda çözüm arka plana kilitlenir; kareler çözülür ama
            elde edilen geometri konu değil ortamdır.
          </li>
          <li>
            <strong>Görüşler arasında boşluk var.</strong> Birkaç noktada durup
            aradaki yolu çekmemek, eşleşme grafiğini kopuk adalara böler. Her ada
            kendi içinde tutarlıdır ve tek bir sonuç kurulamaz.
          </li>
          <li>
            <strong>Kareler bulanık.</strong> Bulanık görüntüde eşleştirilecek
            ayırt edici nokta kalmaz.
          </li>
          <li>
            <strong>Konu düz ve desensiz.</strong> Tek renk bir duvarda veya
            parlak bir yüzeyde eşleştirilecek doku yoktur.
          </li>
          <li>
            <strong>Yeterli görüntü yok.</strong> Çok az kare, eşleştirilecek
            çift bırakmaz.
          </li>
        </ul>
        <Callout tone="caution">
          İyi kapsama başarıyı garanti etmez. Kapsama neyin görüldüğünü ölçer;
          rekonstrüksiyon ise kareler arasında eşleşme kurulabilmesini gerektirir.
          Sekiz ayrı noktadan çekilmiş bir set her bölgeyi görebilir ve yine de
          hiçbir şey kuramaz.
        </Callout>
      </>
    ),
  },
  {
    id: "dosya-turleri",
    title: "Desteklenen dosya türleri",
    icon: IconFiles,
    keywords: ["dosya", "biçim", "heic", "jpeg", "mp4", "format"],
    body: () => (
      <>
        <h3 className="help__subhead">Fotoğraf</h3>
        <LiveFormats mediaType="photo" />
        <h3 className="help__subhead">Video</h3>
        <LiveFormats mediaType="video" />
        <p className="muted">
          Dosyalar yalnızca uzantısına bakılarak kabul edilmez: içeriğinin
          gerçekten o biçim olduğu sunucuda doğrulanır. Uzantısı değiştirilmiş
          bir dosya reddedilir.
        </p>
        <Callout tone="neutral">
          <strong>HEIC / HEIF artık desteklenir.</strong> iPhone varsayılan
          olarak bu biçimde çeker; dönüştürmenize gerek yoktur. Dosya sunucuda
          çözümlenir ve yönü (EXIF) korunur.
        </Callout>
        <p className="muted small">
          Video kapsayıcısının tanınması tek başına yeterli değildir: içindeki
          video biçiminin de çözülebilmesi gerekir. Çözülemezse yükleme,
          dosyanın bozuk olduğunu değil, o video biçiminin desteklenmediğini
          söyleyerek reddedilir.
        </p>
      </>
    ),
  },
  {
    id: "bildirimler",
    title: "Bildirimler",
    icon: IconHelp,
    keywords: ["bildirim", "zil", "uyarı"],
    body: () => (
      <>
        <p className="muted">
          Bildirimler üstteki zil simgesinde toplanır. Yalnızca iki olay bildirim
          üretir:
        </p>
        <ul className="help__list">
          <li>Rekonstrüksiyon tamamlandı</li>
          <li>Rekonstrüksiyon başarısız oldu</li>
        </ul>
        <p className="muted">
          Bunlar, siz başka bir ekrandayken dakikalar sonra biten tek işlerdir.
          Çekim yükleme ve analiz eş zamanlıdır; sonuçları zaten baktığınız
          ekranda görünür, bu yüzden bildirim üretmezler.
        </p>
        <Callout tone="neutral">
          E-posta gönderilmez ve bildirim türleri kapatılamaz. Bildirimler
          yalnızca uygulama içindedir.
        </Callout>
      </>
    ),
  },
  {
    id: "gizlilik",
    title: "Gizlilik ve güvenlik",
    icon: IconLock,
    keywords: ["gizlilik", "güvenlik", "yüz", "kimlik", "izolasyon"],
    body: () => (
      <>
        <ul className="help__list">
          <li>
            <strong>Yüz tanıma yapılmaz.</strong> Analiz yalnızca çekimin
            geometrik yeterliliğini ölçer: yüzün karede olup olmadığı ve kabaca
            hangi yöne baktığı. Kimlik, yüz imzası, karşılaştırma, yaş, cinsiyet
            veya duygu çıkarımı üretilmez ve saklanmaz.
          </li>
          <li>
            <strong>Projeleriniz size aittir.</strong> Her istek hesabınızla
            sınırlanır. Başka bir hesabın projesi, çekimi, dosyası veya
            rekonstrüksiyonu kimlik numarası bilinse bile açılamaz.
          </li>
          <li>
            <strong>Dosya adresleri paylaşılmaz.</strong> Hiçbir yanıt sunucu
            üzerindeki dosya yolunu veya depolama anahtarını içermez; tahmin
            edilebilir genel bağlantı yoktur.
          </li>
          <li>
            <strong>Telefonda kalıcı iz kalmaz.</strong> Eşleştirme tek
            kullanımlıktır ve bittiğinde telefon hakkında hiçbir kayıt tutulmaz.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "hesap",
    title: "Hesap ve oturum",
    icon: IconPerson,
    keywords: ["hesap", "oturum", "parola", "beni hatırla", "çıkış"],
    body: () => (
      <>
        <p className="muted">
          Oturumunuz, JavaScript'in okuyamadığı bir çerezde taşınır. Tarayıcıda
          parola veya oturum anahtarı saklanmaz.
        </p>
        <ul className="help__list">
          <li>
            <strong>Görünen adınızı</strong> Ayarlar → Profil bölümünden
            değiştirebilirsiniz. Ana Sayfa'daki selamlama hemen güncellenir.
          </li>
          <li>
            <strong>Parolanızı</strong> Ayarlar → Parola bölümünden
            değiştirebilirsiniz. Mevcut parolanız sorulur. Değişiklikten sonra
            bu cihazdaki oturumunuz açık kalır, diğer tüm oturumlar
            sonlandırılır — parola değiştirmenin amacı budur.
          </li>
          <li>
            <strong>Açık oturumlarınızı</strong> Ayarlar → Açık oturumlar
            bölümünde görebilir, tek tek veya toplu olarak sonlandırabilirsiniz.
            Sonlandırılan bir oturum o an geçersiz olur; "Beni hatırla" ile
            açılmış oturumlar da dahildir.
          </li>
          <li>
            <strong>Beni hatırla</strong> iki şey yapar: e-posta adresinizi bu
            tarayıcıda saklar, böylece giriş formu dolu gelir; ve sunucudan daha
            uzun ömürlü bir oturum ister. Kayıtlı adresi{" "}
            <strong>Ayarlar</strong> ekranından silebilirsiniz.
          </li>
          <li>
            <strong>Çıkış</strong> oturumu sunucu tarafında sonlandırır.
          </li>
        </ul>
        <Callout tone="neutral">
          E-posta adresi değişikliği ve iki adımlı doğrulama bu sürümde yoktur.
          Oturumunuz, JavaScript'in okuyamadığı bir çerezde taşınır.
        </Callout>
      </>
    ),
  },
  {
    id: "gorunum",
    title: "Görünüm ve tema",
    icon: IconEye,
    keywords: ["tema", "koyu", "açık", "karanlık", "görünüm"],
    body: () => (
      <>
        <p className="muted">
          Ayarlar → Görünüm bölümünden arayüz temasını seçebilirsiniz:
        </p>
        <ul className="help__list">
          <li>
            <strong>Sistem</strong> — işletim sisteminizin açık/koyu tercihini
            izler. Varsayılan budur.
          </li>
          <li>
            <strong>Koyu</strong> — işletim sistemi ne derse desin koyu.
          </li>
          <li>
            <strong>Açık</strong> — işletim sistemi ne derse desin açık.
          </li>
        </ul>
        <Callout tone="neutral">
          Bu tercih yalnızca kullandığınız tarayıcıda saklanır; hesabınıza
          kaydedilmez ve başka bir cihazda açtığınızda geçerli olmaz. Seçim
          anında uygulanır, kaydedilecek bir şey yoktur.
        </Callout>
      </>
    ),
  },
  {
    id: "sorun-giderme",
    title: "Sorun giderme",
    icon: IconAlert,
    keywords: ["sorun", "hata", "çalışmıyor", "açılmıyor"],
    body: () => (
      <>
        <Problem symptom="Telefondaki QR sayfası açılmıyor">
          Telefon ile bilgisayarın aynı ağda olduğundan emin olun. Kodlar kısa
          süre geçerlidir ve yalnızca bir kez kullanılabilir; süresi geçtiyse
          Çekimler ekranından yeni bir kod oluşturun.
        </Problem>
        <Problem symptom="Kamera izni verilmedi ya da kamera açılmıyor">
          Telefon sayfası güvenli (HTTPS) bir adreste değilse tarayıcı kamerayı
          hiç vermez; sayfa bunu yazar. Adres HTTPS ise, tarayıcının adres
          çubuğundaki site izinlerinden kamerayı yeniden verin ve sayfayı
          yenileyin. Kamera başka bir uygulamada açıksa onu kapatın.
        </Problem>
        <Problem symptom="Canlı rehberlik çalışmıyor">
          Rehberlik bir kapı değildir: çalışmasa da çekime devam edebilirsiniz.
          Kareler normal şekilde yüklenir.
        </Problem>
        <Problem symptom="Analiz başlatılamıyor">
          Analiz için projede en az bir yüklenmiş görüntü gerekir. Yalnızca video
          yüklediyseniz önce videoyu hazırlamanız gerekir; analiz seçilmiş
          kareler üzerinde çalışır.
        </Problem>
        <Problem symptom="Video hazırlanamadı">
          Dosya bozuk veya desteklenmeyen bir kayıt olabilir. Kamera hiç hareket
          etmediyse ayrılabilecek farklı kare de bulunmayabilir. Videoyu MP4'e
          dönüştürüp yeniden yüklemeyi deneyin.
        </Problem>
        <Problem symptom="Rekonstrüksiyon başarısız oldu">
          En sık nedenler ayrı bir başlıkta toplanmıştır:{" "}
          <em>Rekonstrüksiyon neden başarısız olur?</em> Çoğu durumda çözüm yeni
          bir çekimdir: kamerayı konunun çevresinde durmadan hareket ettirerek,
          ara noktaları atlamadan.
        </Problem>
        <Problem symptom="Dosya önizlemesi oluşturulamadı">
          Önizleme, yüklediğiniz dosyadan istendiği anda üretilir: fotoğraflar
          küçültülerek, videolar bir kare çıkarılarak. Üretilemediğinde yerine
          sahte bir görsel konmaz — dosyanın kendisi etkilenmez ve çekim,
          analiz ve rekonstrüksiyon normal şekilde çalışmaya devam eder.
        </Problem>
        <Problem symptom="Bildirim gelmiyor">
          Yalnızca rekonstrüksiyonun bitmesi veya başarısız olması bildirim
          üretir. Çekim yükleme ve analiz bildirim üretmez. E-posta gönderilmez.
        </Problem>
        <Problem symptom="Yüklediğim dosya reddedildi">
          Kabul edilen biçimlerin tam listesi <em>Desteklenen dosya türleri</em>{" "}
          başlığındadır ve sunucudan okunur. Uzantısı içeriğiyle uyuşmayan
          dosyalar da reddedilir: SentyCap dosyanın adına değil, içine bakar.
        </Problem>
        <Callout tone="neutral">
          Hiçbir sorun için tarayıcı güvenlik ayarlarını kapatmanız istenmez.
          Kamera ve güvenli bağlantı kuralları tarayıcının kendi kurallarıdır.
        </Callout>
      </>
    ),
  },
];

export const DEFAULT_TOPIC_ID = HELP_TOPICS[0]!.id;

/** Topics whose title or keywords contain the needle. Empty needle: all. */
export function searchTopics(needle: string): HelpTopic[] {
  const query = needle.trim().toLocaleLowerCase("tr");
  if (query === "") return HELP_TOPICS;
  return HELP_TOPICS.filter(
    (topic) =>
      topic.title.toLocaleLowerCase("tr").includes(query) ||
      topic.keywords.some((word) => word.toLocaleLowerCase("tr").includes(query)),
  );
}
